import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { StaticCollider } from '../physics/body';
import type { System } from '../core/system';
import { buildLowBorough } from './lowBorough/build';
import { districtAt } from './lowBorough/data';
import { createSignalLights, type SignalLights } from './signalControl';
import type { SignalColour } from './signals';
import type { CityLayout } from './types';
import type { PropDecor, SignalHeadDecor } from './decor';
import { buildCityView, type CityView } from './view/cityView';
import { loadModelPack } from '../render/models/modelLibrary';
import { ktx2Loader } from '../render/ktx2';
import { sealFirstView } from '../render/shaderWarmup';
import { requestWheelModels } from '../vehicles/rimModels';
import { loadingProgress } from '../ui/loading';
import { MAX_KNOCKED, type KnockKind } from './view/knockdowns';

declare module '../core/services' {
  interface ServiceMap {
    /** The district layout (read-only for everyone except src/world). */
    layout: CityLayout;
  }
}

declare module '../core/events' {
  interface GameEvents {
    /** A car took out a street lamp (visual + audio cue; emitted by world). */
    lampBroken: { pos: { x: number; z: number }; byPlayer: boolean; speed: number };
    /** A car knocked a lamp post, signal pole or Belisha beacon over (emitted by world). */
    furnitureKnocked: { kind: 'lamp' | 'signal' | 'belisha'; pos: { x: number; z: number }; byPlayer: boolean; speed: number };
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Signalled junctions with the colour the lamps show on each approach. */
    signalLamps(): { id: number; nodeId: number; colours: Record<number, SignalColour> }[];
    /** District id at a point (UI / debugging). */
    districtAt(x: number, z: number): string;
  }
}

/** The authored world is fixed: the same Low Borough for every game seed. */
export const WORLD_SEED = 1971;

/**
 * Knockable street furniture (DRIVING-3). A vehicle hitting a lamp post, signal pole or Belisha
 * beacon with at least KNOCK_MOMENTUM along the contact normal (and KNOCK_MIN_SPEED closing speed)
 * knocks it over: the static collider is removed and the car only loses POST_IMPULSE of momentum,
 * so a lorry ploughs on (-0.5 m/s), a saloon loses ~2 m/s, slow taps still stop you.
 */
export const KNOCK_MOMENTUM = 8000; // N·s: compact (780 kg) 10.3 m/s, saloon (1450) 5.5, lorry 4 (min)
export const KNOCK_MIN_SPEED = 4; // m/s
export const POST_IMPULSE = 2800; // N·s absorbed by the post
/** How long boot waits for the street pack (ms) before building the city with the procedural props. */
const STREET_PACK_WAIT_MS = 6000;
/** The wheels pack (~170 KB) is requested with the street pack; this is only the extra wait after it. */
const WHEELS_PACK_WAIT_MS = 1500;
const KNOCK_TAGS: Record<string, KnockKind> ={ 'prop:lamp': 'lamp', 'prop:signal': 'signal', 'prop:belisha': 'belisha' };

/**
 * World system (owner: world-artist). Provides `layout` (incl. authored `layout.props` for the props
 * module) and `signalVisuals`, registers static colliders and builds the city visuals. Order 100: before every gameplay system.
 */
export function createWorldSystem(): System {
  let ctx: GameContext;
  let view: CityView | null = null;
  let lights: SignalLights;
  const focus = new THREE.Vector3();

  return {
    id: 'world',
    order: 100,
    async init(c) {
      ctx = c;
      if (c.render) {
        // The street pack, the wheels and the KTX2 transcoder download while the layout is generated (CPU), not
        // after it: the pack's textures no longer wait for the pack to land before the transcoder is asked for.
        ktx2Loader().init().catch(() => undefined); // a failed fetch is reported to the loads that need it
        void loadModelPack('street', 0, (f) => loadingProgress(0.05 + f * 0.6));
        // The Blender wheels and the police lamp (vehicles/rimModels.ts) travel alongside: every car wants them,
        // and a body built before the lamp is in keeps the procedural one.
        requestWheelModels();
      }
      const { layout, decor } = buildLowBorough(WORLD_SEED);
      c.services.provide('layout', layout);
      // Authored knock-about props are simulated and drawn by the props module (src/props).
      if (!layout.props) {
        const kind = { bin: 'bin', barrel: 'barrel', crate: 'crate', box: 'crate', cone: 'cone', barrier: 'barrier' } as const;
        layout.props = decor.looseProps.map((p) => ({ kind: kind[p.type], pos: { ...p.pos }, heading: p.heading }));
      }
      lights = createSignalLights(layout, () => c.state.time);
      c.services.provide('signalVisuals', lights);
      const lampStatics = new Map<number, { x: number; z: number }>();
      const knockables = new Map<number, { kind: KnockKind; st: StaticCollider; item: PropDecor | SignalHeadDecor; index: number }>();
      const near = (a: { x: number; z: number }, b: { x: number; z: number }): boolean => Math.abs(a.x - b.x) < 0.01 && Math.abs(a.z - b.z) < 0.01;
      for (const col of layout.colliders) {
        const st = c.physics.addStatic(col);
        if (col.kind === 'circle' && (col.tag === 'prop:lamp' || col.tag === 'prop:bridgeLamp')) lampStatics.set(st.id, { ...col.center });
        const kind = col.tag ? KNOCK_TAGS[col.tag] : undefined;
        if (kind && col.kind === 'circle') {
          const index = kind === 'signal' ? decor.signalHeads.findIndex((h) => near(h.pos, col.center)) : decor.props.findIndex((p) => p.type === kind && near(p.pos, col.center));
          const item = kind === 'signal' ? decor.signalHeads[index] : decor.props[index];
          if (item) knockables.set(st.id, { kind, st, item, index });
        }
      }
      if (c.render) {
        // The Meshy street pack (trees, bandstand, phone box …; render/models) is merged into the city chunks,
        // so it is awaited here, behind the loading screen, for at most STREET_PACK_WAIT_MS: a slow or missing
        // pack leaves the procedural props (the city is never left with a hole).
        // The loading bar follows the download (and the KTX2 transcoder's first start), then the build.
        loadingProgress(0.05, 'Unloading the lorries');
        await loadModelPack('street', STREET_PACK_WAIT_MS);
        await loadModelPack('wheels', WHEELS_PACK_WAIT_MS);
        loadingProgress(0.7, 'Laying the setts');
        await new Promise<void>((r) => setTimeout(r, 0));
        view = buildCityView(c.scene, layout, decor, lights);
        // the city's first-view loads are all registered: the held model downloads go once they are in
        sealFirstView();
      }
      let knocked = 0;

      // Lamp posts, signal poles and Belisha beacons give way to a hard hit (before vehicle damage
      // runs: world listens first, and trims the reported impulse to what the post absorbed).
      c.events.on('collision', (e) => {
        const staticIsB = e.b?.kind === 'static';
        const stTag = staticIsB ? e.b : e.a?.kind === 'static' ? e.a : null;
        const other = staticIsB ? e.a : e.b;
        if (!stTag || !other || other.kind !== 'vehicle') return;
        const k = knockables.get(stTag.id);
        if (!k || knocked >= MAX_KNOCKED) return;
        const body = c.physics.getBody(other.bodyId);
        if (!body || body.mass <= 0) return;
        if (e.speed < KNOCK_MIN_SPEED || body.mass * e.speed < KNOCK_MOMENTUM) return;
        knockables.delete(stTag.id);
        lampStatics.delete(stTag.id);
        c.physics.removeStatic(k.st);
        knocked++;
        // Normal pointing from the vehicle into the post; the solver pushed the car back along -n.
        const nx = staticIsB ? e.normal.x : -e.normal.x;
        const nz = staticIsB ? e.normal.z : -e.normal.z;
        const giveBack = Math.max(0, e.impulse - POST_IMPULSE);
        if (giveBack > 0) body.applyImpulse({ x: nx * giveBack, z: nz * giveBack }, e.pos);
        e.impulse = Math.min(e.impulse, POST_IMPULSE);
        // Falls the way the car was going (contact normal blended with the car's travel).
        const sp = Math.hypot(body.vel.x, body.vel.z);
        let fx = nx + (sp > 0.5 ? body.vel.x / sp : 0);
        let fz = nz + (sp > 0.5 ? body.vel.z / sp : 0);
        const fl = Math.hypot(fx, fz) || 1;
        fx /= fl;
        fz /= fl;
        const at = { x: k.item.pos.x, z: k.item.pos.z };
        const veh = c.services.tryGet('vehicles')?.get(other.id);
        const byPlayer = veh?.controller === 'player';
        if (view) {
          view.knockdowns.knock(k.kind, k.item, { x: fx, z: fz });
          if (k.kind === 'signal') view.signalHeads.knockOut(k.index);
          if (k.kind === 'lamp') {
            const idx = view.lamps.lampAt(at);
            if (idx >= 0 && view.lamps.breakLamp(idx)) c.events.emit('lampBroken', { pos: { ...at }, byPlayer, speed: e.speed });
          }
          const particles = c.services.tryGet('particles');
          particles?.emit('sparks', { x: at.x, y: 0.6, z: at.z }, { count: 14, intensity: 1 });
          if (k.kind !== 'belisha') particles?.emit('glass', { x: at.x + fx * 2, y: 1.2, z: at.z + fz * 2 }, { count: 10 });
        }
        c.events.emit('furnitureKnocked', { kind: k.kind, pos: at, byPlayer, speed: e.speed });
      });

      // Street lamps get knocked out by hard hits: sparks, glass, a sputter, then dark.
      c.events.on('collision', (e) => {
        if (!view || e.speed < 6.5) return;
        const lampSide = e.a?.kind === 'static' ? e.a : e.b?.kind === 'static' ? e.b : null;
        const other = lampSide === e.a ? e.b : e.a;
        if (!lampSide || !other || other.kind !== 'vehicle') return;
        const at = lampStatics.get(lampSide.id);
        if (!at) return;
        const idx = view.lamps.lampAt(at);
        if (idx < 0 || !view.lamps.breakLamp(idx)) return;
        const lamp = view.lamps.lamps[idx]!;
        const hx = at.x + Math.sin(lamp.heading) * 1.25;
        const hz = at.z + Math.cos(lamp.heading) * 1.25;
        const particles = c.services.tryGet('particles');
        particles?.emit('sparks', { x: hx, y: 5.8, z: hz }, { count: 26, intensity: 1.3 });
        particles?.emit('glass', { x: hx, y: 5.6, z: hz }, { count: 14 });
        particles?.emit('sparks', { x: at.x, y: 0.6, z: at.z }, { count: 10, intensity: 0.8 });
        const veh = c.services.tryGet('vehicles')?.get(other.id);
        c.events.emit('lampBroken', { pos: { ...at }, byPlayer: veh?.controller === 'player', speed: e.speed });
      });

      c.hooks.register('signalLamps', () =>
        layout.intersections
          .filter((it) => it.signal)
          .map((it) => {
            const colours: Record<number, SignalColour> = {};
            for (const e of it.edgeIds) colours[e] = lights.colour(it.id, e);
            return { id: it.id, nodeId: it.nodeId, colours };
          }),
      );
      c.hooks.register('districtAt', (x, z) => districtAt({ x, z }));
      c.hooks.registerSnapshot('world', () => ({
        name: layout.name,
        districts: layout.districts.map((d) => d.id),
        buildings: layout.buildings.length,
        colliders: layout.colliders.length,
        edges: layout.roads.edges.length,
        ...(view ? { view: view.stats() } : {}),
      }));
    },
    update(dt) {
      if (!view) return;
      view.update(ctx.state.time, dt);
      // Line-of-sight cut-away: keep the player (or their car) visible behind frontages, roofs and
      // canopies on the camera side. Uses this frame's player position and last frame's camera.
      const player = ctx.services.tryGet('player');
      const rig = ctx.services.tryGet('cameraRig');
      if (rig?.isCinematic || !player) {
        if (rig) view.occluder.update(ctx.camera.position, focus.set(rig.lookAt.x, 1, rig.lookAt.z), 3);
        else view.occluder.disable();
        return;
      }
      const car = player.mode === 'driving' || player.mode === 'exiting' ? player.vehicle : null;
      const radius = car ? Math.max(2.8, car.spec.length / 2 + 1.0) : 2.0;
      view.occluder.update(ctx.camera.position, focus.set(player.pos.x, car ? 1.0 : 1.1, player.pos.z), radius);
    },
    dispose() {
      view?.dispose();
    },
  };
}
